import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface FAQSection {
  id: string;
  name: string;
  description?: string | null;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by?: string | null;
  faqs?: FAQ[];
}

export interface FAQ {
  id: string;
  section_id: string;
  question: string;
  answer: string;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by?: string | null;
}

export const useFAQSections = () => {
  return useQuery({
    queryKey: ["faq-sections"],
    queryFn: async (): Promise<FAQSection[]> => {
      const { data, error } = await supabase
        .from("faq_sections")
        .select(`
          *,
          faqs (*)
        `)
        .eq("is_active", true)
        .order("sort_order", { ascending: true });

      if (error) throw error;
      
      // Sort FAQs within each section
      return (data || []).map(section => ({
        ...section,
        faqs: (section.faqs || [])
          .filter((faq: FAQ) => faq.is_active)
          .sort((a: FAQ, b: FAQ) => a.sort_order - b.sort_order)
      }));
    },
  });
};

export const useAllFAQSections = () => {
  return useQuery({
    queryKey: ["faq-sections-all"],
    queryFn: async (): Promise<FAQSection[]> => {
      const { data, error } = await supabase
        .from("faq_sections")
        .select("*")
        .order("sort_order", { ascending: true });

      if (error) throw error;
      return data || [];
    },
  });
};

export const useCreateFAQSection = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      name,
      description,
      sort_order,
      is_active,
    }: {
      name: string;
      description?: string;
      sort_order?: number;
      is_active?: boolean;
    }) => {
      const { data, error } = await supabase
        .from("faq_sections")
        .insert({
          name,
          description,
          sort_order: sort_order ?? 0,
          is_active: is_active ?? true,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["faq-sections"] });
      queryClient.invalidateQueries({ queryKey: ["faq-sections-all"] });
      toast({
        title: "Success",
        description: "FAQ section created successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to create FAQ section",
        variant: "destructive",
      });
      console.error("Create FAQ section error:", error);
    },
  });
};

export const useUpdateFAQSection = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      id,
      name,
      description,
      sort_order,
      is_active,
    }: {
      id: string;
      name?: string;
      description?: string | null;
      sort_order?: number;
      is_active?: boolean;
    }) => {
      const { data, error } = await supabase
        .from("faq_sections")
        .update({
          name,
          description,
          sort_order,
          is_active,
        })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["faq-sections"] });
      queryClient.invalidateQueries({ queryKey: ["faq-sections-all"] });
      toast({
        title: "Success",
        description: "FAQ section updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update FAQ section",
        variant: "destructive",
      });
      console.error("Update FAQ section error:", error);
    },
  });
};

export const useDeleteFAQSection = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("faq_sections")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["faq-sections"] });
      queryClient.invalidateQueries({ queryKey: ["faq-sections-all"] });
      toast({
        title: "Success",
        description: "FAQ section deleted successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to delete FAQ section",
        variant: "destructive",
      });
      console.error("Delete FAQ section error:", error);
    },
  });
};
